import React from "react";
import { Link } from "react-router-dom";
import "./Term.css";

import Header from "./Header";
import Footer from "./Footer";

const LAST_UPDATED = "July 20, 2026";

const sections = [
  { id: "acceptance", title: "1. Acceptance of Terms" },
  { id: "eligibility", title: "2. Eligibility" },
  { id: "accounts", title: "3. Accounts & Business Listings" },
  { id: "benefits", title: "4. What You Get" },
  { id: "your-responsibilities", title: "5. Your Responsibilities" },
  { id: "our-responsibilities", title: "6. Our Responsibilities" },
  { id: "listings-disclaimer", title: "7. Business Listings Disclaimer" },
  { id: "hubmart", title: "8. HubMart & Transactions" },
  { id: "content", title: "9. User Content & License" },
  { id: "prohibited", title: "10. Prohibited Conduct" },
  { id: "ip", title: "11. Intellectual Property" },
  { id: "privacy", title: "12. Privacy" },
  { id: "third-party", title: "13. Third-Party Links & Services" },
  { id: "disclaimers", title: "14. Disclaimers" },
  { id: "liability", title: "15. Limitation of Liability" },
  { id: "indemnification", title: "16. Indemnification" },
  { id: "termination", title: "17. Termination" },
  { id: "governing-law", title: "18. Governing Law & Disputes" },
  { id: "changes", title: "19. Changes to These Terms" },
  { id: "contact", title: "20. Contact Us" },
];

function TermsPage() {
  return (
    <>
      <Header />

      <div className="terms-page" id="terms">
        {/* ── Page header ── */}
        <p className="terms-eyebrow">Legal</p>
        <h2 className="terms-title">Terms &amp; Conditions</h2>
        <p className="terms-updated">Last updated: {LAST_UPDATED}</p>
        <p className="terms-intro">
          Welcome to <span className="brand">GoOurHub</span>   your community
          marketplace. These Terms &amp; Conditions ("Terms") govern your access
          to and use of the GoOurHub website, mobile experience, HubMart
          marketplace, and related services (collectively, the "Platform").
          Please read them carefully. For quick answers, see our{" "}
          <Link to="/faq" className="terms-link">FAQs</Link> page.
        </p>

        {/* ── Table of contents ── */}
        <nav className="terms-toc" aria-label="Table of contents">
          <p className="toc-heading">On this page</p>
          <ol>
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`}>{s.title}</a>
              </li>
            ))}
          </ol>
        </nav>

        {/* ── 1. Acceptance ── */}
        <section id="acceptance" className="terms-section">
          <h3>1. Acceptance of Terms</h3>
          <p>
            By accessing or using the Platform, creating an account, or listing
            a business, you agree to be bound by these Terms and our policies
            referenced within them. If you do not agree, please do not use the
            Platform. If you use the Platform on behalf of a business or
            organization, you represent that you have the authority to bind
            that entity to these Terms.
          </p>
        </section>

        {/* ── 2. Eligibility ── */}
        <section id="eligibility" className="terms-section">
          <h3>2. Eligibility</h3>
          <ul>
            <li>You must be at least 18 years of age to create an account or list a business.</li>
            <li>You must have the legal authority to represent and add the business you list.</li>
            <li>You must provide accurate, current, and complete information during registration.</li>
          </ul>
        </section>

        {/* ── 3. Accounts & Listings ── */}
        <section id="accounts" className="terms-section">
          <h3>3. Accounts &amp; Business Listings</h3>
          <p>
            You are responsible for maintaining the confidentiality of your
            account credentials and for all activity that occurs under your
            account. Notify us promptly at{" "}
            <a href="mailto:contact@goourhub.com" className="terms-link">
              contact@goourhub.com
            </a>{" "}
            if you suspect unauthorized use. We reserve the right to approve,
            decline, edit for formatting, or remove any listing at our
            discretion to keep the Platform safe and trustworthy.
          </p>
        </section>

        {/* ── 4. Benefits ── */}
        <section id="benefits" className="terms-section">
          <h3>4. What You Get</h3>
          <ul>
            <li>Greater online visibility across diverse communities   all in one place.</li>
            <li>Your own dedicated business page on GoOurHub.</li>
            <li>Exposure to new customers through our platform and social channels (Instagram, Facebook, TikTok, and others).</li>
            <li>Optional participation in HubMart, community events, and promotional features.</li>
          </ul>
        </section>

        {/* ── 5. Your Responsibilities ── */}
        <section id="your-responsibilities" className="terms-section">
          <h3>5. Your Responsibilities</h3>
          <ul>
            <li>Keep your business content accurate and updated in a timely manner.</li>
            <li>Ensure you hold all rights, licenses, and permits required to operate and promote your business.</li>
            <li>Ensure any content you submit (text, images, logos, offers) does not infringe the rights of others.</li>
            <li>Comply with all applicable local, state, and federal laws.</li>
          </ul>
        </section>

        {/* ── 6. Our Responsibilities ── */}
        <section id="our-responsibilities" className="terms-section">
          <h3>6. Our Responsibilities</h3>
          <p>
            We provide marketing and visibility services, contact and account
            management support, and a platform for community discovery and
            connection. GoOurHub is a promotional and discovery platform only  
            we do not own, operate, endorse, or control the businesses listed,
            and we are not a party to any transaction between you and a
            business or customer.
          </p>
        </section>

        {/* ── 7. Listings Disclaimer ── */}
        <section id="listings-disclaimer" className="terms-section">
          <h3>7. Business Listings Disclaimer</h3>
          <p>
            GoOurHub displays business listings compiled from publicly
            available sources, directories, and community submissions. While we
            strive for accuracy, we do not guarantee the completeness,
            verification, or timeliness of business information unless a
            listing has been officially claimed by its owner.
          </p>
          <p>
            If your business is listed and you would like to claim, update, or
            remove it, please use our{" "}
            <Link to="/partner-signup#joinForm" className="terms-link">
              <strong>Claim / Opt-Out / Suggest Edits form</strong>
            </Link>
            . We honor legitimate removal requests promptly.
          </p>
          <p>
            By using the Platform, you acknowledge that listings may include
            unverified content sourced from public records or community
            submissions.
          </p>
        </section>

        {/* ── 8. HubMart ── */}
        <section id="hubmart" className="terms-section">
          <h3>8. HubMart &amp; Transactions</h3>
          <p>
            HubMart connects community vendors with customers. Unless expressly
            stated otherwise, all purchases, payments, deliveries, returns, and
            warranties are solely between the buyer and the vendor. Vendors are
            responsible for the quality, safety, legality, and accurate
            description of their products and for collecting and remitting any
            applicable taxes. GoOurHub does not guarantee any product, service,
            or outcome offered through HubMart.
          </p>
        </section>

        {/* ── 9. User Content ── */}
        <section id="content" className="terms-section">
          <h3>9. User Content &amp; License</h3>
          <p>
            You retain ownership of the content you submit. By submitting
            content to the Platform, you grant GoOurHub a non-exclusive,
            worldwide, royalty-free license to host, display, reproduce, and
            promote that content on the Platform and our marketing channels for
            the purpose of operating and promoting the Platform and your
            listing. You may request removal of your content at any time by
            contacting us.
          </p>
        </section>

        {/* ── 10. Prohibited Conduct ── */}
        <section id="prohibited" className="terms-section">
          <h3>10. Prohibited Conduct</h3>
          <ul>
            <li>Posting false, misleading, defamatory, or infringing content.</li>
            <li>Listing illegal products or services, or engaging in fraudulent activity.</li>
            <li>Impersonating another person or business, or claiming a listing you are not authorized to manage.</li>
            <li>Scraping, harvesting data, or interfering with the Platform's security or operation.</li>
            <li>Harassing, discriminating against, or harming other users or communities.</li>
          </ul>
        </section>

        {/* ── 11. IP ── */}
        <section id="ip" className="terms-section">
          <h3>11. Intellectual Property</h3>
          <p>
            The GoOurHub name, logo, HubMart name, design, and all Platform
            content created by us are the property of GoOurHub and are
            protected by applicable intellectual-property laws. You may not use
            our branding without prior written permission.
          </p>
        </section>

        {/* ── 12. Privacy ── */}
        <section id="privacy" className="terms-section">
          <h3>12. Privacy</h3>
          <p>
            Your use of the Platform is also governed by our{" "}
            <Link to="/privacy" className="terms-link">Privacy Policy</Link>,
            which describes how we collect, use, and protect your information.
          </p>
        </section>

        {/* ── 13. Third-Party Links ── */}
        <section id="third-party" className="terms-section">
          <h3>13. Third-Party Links &amp; Services</h3>
          <p>
            The Platform may contain links to third-party websites, social
            media pages, or services. We are not responsible for the content,
            policies, or practices of any third party, and a link does not
            imply endorsement.
          </p>
        </section>

        {/* ── 14. Disclaimers ── */}
        <section id="disclaimers" className="terms-section">
          <h3>14. Disclaimers</h3>
          <p>
            The Platform is provided on an "as is" and "as available" basis
            without warranties of any kind, express or implied, including
            warranties of merchantability, fitness for a particular purpose,
            and non-infringement. We do not warrant that the Platform will be
            uninterrupted, error-free, or secure.
          </p>
        </section>

        {/* ── 15. Liability ── */}
        <section id="liability" className="terms-section">
          <h3>15. Limitation of Liability</h3>
          <p>
            To the maximum extent permitted by law, GoOurHub and its founders,
            employees, and partners shall not be liable for any indirect,
            incidental, special, consequential, or punitive damages   or any
            loss of profits, data, goodwill, or business opportunity   arising
            from your use of, or inability to use, the Platform, including
            mistakes, errors, omissions, or technical problems. Where liability
            cannot be excluded, our total liability shall not exceed the amount
            you paid to GoOurHub (if any) in the twelve months preceding the
            claim.
          </p>
        </section>

        {/* ── 16. Indemnification ── */}
        <section id="indemnification" className="terms-section">
          <h3>16. Indemnification</h3>
          <p>
            You agree to indemnify and hold harmless GoOurHub from any claims,
            damages, liabilities, and expenses (including reasonable attorneys'
            fees) arising from your content, your business, your use of the
            Platform, or your violation of these Terms.
          </p>
        </section>

        {/* ── 17. Termination ── */}
        <section id="termination" className="terms-section">
          <h3>17. Termination</h3>
          <p>
            You may stop using the Platform or request removal of your listing
            at any time. We may suspend or terminate access to the Platform, or
            remove content, at our discretion   including for violations of
            these Terms. Sections that by their nature should survive
            termination (such as licenses already exercised, disclaimers,
            liability limits, and indemnification) will survive.
          </p>
        </section>

        {/* ── 18. Governing Law ── */}
        <section id="governing-law" className="terms-section">
          <h3>18. Governing Law &amp; Disputes</h3>
          <p>
            These Terms are governed by the laws of the State of Texas, without
            regard to conflict-of-law principles. You agree to first contact us
            to attempt to resolve any dispute informally. Any dispute that
            cannot be resolved informally shall be brought in the state or
            federal courts located in Bexar County, Texas.
          </p>
        </section>

        {/* ── 19. Changes ── */}
        <section id="changes" className="terms-section">
          <h3>19. Changes to These Terms</h3>
          <p>
            We may update these Terms from time to time. When we do, we will
            revise the "Last updated" date above and, for material changes,
            provide reasonable notice on the Platform. Continued use of the
            Platform after changes take effect constitutes acceptance of the
            updated Terms.
          </p>
        </section>

        {/* ── 20. Contact ── */}
        <section id="contact" className="terms-section">
          <h3>20. Contact Us</h3>
          <p>
            Questions about these Terms? We're happy to help.
            <br />
            Support:{" "}
            <a href="mailto:contact@goourhub.com" className="terms-link">
              contact@goourhub.com
            </a>
          </p>
        </section>

        <p className="terms-footer-note">
          ★ GoOurHub   Where You Belong ★
        </p>
      </div>

      <Footer />
    </>
  );
}

export default TermsPage;
